/**
 * Combo multi-opción de etiquetas de origen (Meta / No Meta).
 * Grupos y orden = Configuración → Orígenes.
 * @module-isolated analytics-shared
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';

const GROUP_ORDER = ['Especial', 'Meta', 'No Meta', 'Etiquetas', 'Sin clasificar', 'Otros'];

/**
 * @param {{
 *   options: Array<{id:string,label:string,isMeta?:boolean|null,group?:string,order?:number,count?:number}>,
 *   value: string[],
 *   onChange: (next: string[]) => void,
 *   disabled?: boolean,
 *   loading?: boolean,
 *   label?: string,
 *   emptySummary?: string,
 *   onSelectMetaOnly?: () => void,
 *   onSelectAll?: () => void,
 *   onClear?: () => void
 * }} props
 */
export default function OriginTagsFilter({
  options = [],
  value = [],
  onChange,
  disabled = false,
  loading = false,
  label = 'Orígenes',
  emptySummary = 'Ninguna',
  onSelectMetaOnly,
  onSelectAll,
  onClear
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selectedSet = useMemo(() => new Set((value || []).map(String)), [value]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (ev) => {
      if (rootRef.current && !rootRef.current.contains(ev.target)) setOpen(false);
    };
    const onKey = (ev) => {
      if (ev.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const summary = useMemo(() => {
    if (loading) return 'Cargando…';
    if (!options.length) return 'Sin etiquetas';
    const n = value.length;
    if (!n) return emptySummary;

    const metaOpts = options.filter((o) => o.isMeta === true);
    const otherOpts = options.filter((o) => o.isMeta === false);
    const metaIds = metaOpts.map((o) => o.id);
    const otherIds = otherOpts.map((o) => o.id);

    const metaSel = metaIds.filter((m) => selectedSet.has(m)).length;
    const otherSel = otherIds.filter((m) => selectedSet.has(m)).length;
    const extraSel = value.filter(
      (v) => !metaIds.includes(v) && !otherIds.includes(v)
    ).length;

    const onlyMeta =
      metaIds.length > 0 &&
      n === metaIds.length &&
      metaIds.every((m) => selectedSet.has(m));
    if (onlyMeta) return 'Solo Meta (' + n + ')';

    const onlyOther =
      otherIds.length > 0 &&
      n === otherIds.length &&
      otherIds.every((m) => selectedSet.has(m));
    if (onlyOther) return 'Solo No Meta (' + n + ')';

    if (n === 1) {
      const opt = options.find((o) => o.id === value[0]);
      return (opt && opt.label) || value[0];
    }

    if (n === options.length) return 'Todas (' + n + ')';

    const parts = [];
    if (metaSel) parts.push(metaSel + ' Meta');
    if (otherSel) parts.push(otherSel + ' No Meta');
    if (extraSel) parts.push(extraSel + ' seleccionadas');
    if (parts.length) return parts.join(' · ');
    return n + ' etiquetas';
  }, [loading, options, value, selectedSet, emptySummary]);

  function toggle(idOpt) {
    const next = new Set(selectedSet);
    if (next.has(idOpt)) next.delete(idOpt);
    else next.add(idOpt);
    onChange(Array.from(next));
  }

  const groups = useMemo(() => {
    const map = new Map();
    options.forEach((o) => {
      const g = o.group || 'Otros';
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(o);
    });
    map.forEach((list) => {
      list.sort((a, b) => {
        const ao = Number(a.order) || 0;
        const bo = Number(b.order) || 0;
        if (ao !== bo) return ao - bo;
        return String(a.label || '').localeCompare(String(b.label || ''), 'es');
      });
    });
    return GROUP_ORDER.filter((g) => map.has(g))
      .concat([...map.keys()].filter((g) => !GROUP_ORDER.includes(g)))
      .map((g) => [g, map.get(g)]);
  }, [options]);

  return (
    <div className="analytics-field analytics-origin-filter" ref={rootRef}>
      <span id={id + '-label'}>{label}</span>
      <button
        type="button"
        className={'analytics-origin-filter-trigger' + (open ? ' is-open' : '')}
        disabled={disabled || loading}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={id + '-label'}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="analytics-origin-filter-summary">{summary}</span>
        <span className="analytics-origin-filter-caret" aria-hidden>
          ▾
        </span>
      </button>
      {open ? (
        <div className="analytics-origin-filter-panel" role="listbox" aria-multiselectable="true">
          <div className="analytics-origin-filter-actions">
            {onSelectMetaOnly ? (
              <button type="button" className="analytics-btn-link" onClick={onSelectMetaOnly}>
                Solo Meta
              </button>
            ) : null}
            {onSelectAll ? (
              <button type="button" className="analytics-btn-link" onClick={onSelectAll}>
                Todas
              </button>
            ) : null}
            {onClear ? (
              <button type="button" className="analytics-btn-link" onClick={onClear}>
                Limpiar
              </button>
            ) : null}
          </div>
          {groups.map(([group, opts]) => (
            <div key={group} className="analytics-origin-filter-group">
              <div className="analytics-origin-filter-group-label">{group}</div>
              {opts.map((o) => {
                const checked = selectedSet.has(o.id);
                const kind =
                  o.isMeta === true ? ' is-meta' : o.isMeta === false ? ' is-other' : '';
                const countSuffix =
                  o.count != null && o.count !== '' ? ' (' + o.count + ')' : '';
                return (
                  <label key={o.id} className={'analytics-origin-filter-option' + kind}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(o.id)}
                    />
                    <span>
                      {o.label}
                      {countSuffix}
                    </span>
                  </label>
                );
              })}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
